// @flow
import * as React from 'react';
import {
    canConnect,
    createLineCallback,
    getLineBetweenTwoPoints, getPointColor, getPointProvider,
} from 'esm-web-pro/containers/pages/network/Util';
import type { LeafletContext } from 'react-leaflet/lib/types';
import L from 'leaflet';
import { DRAW_FG, EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';
import highlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import { pointsCoordinatesHighlightCallback } from 'esm-web-pro/containers/pages/network/highlight';
import { get, isEmpty, isEqual } from 'lodash';
import { withLeaflet } from 'react-leaflet';
import UseHighlightLine, { layerIsFromPath } from 'esm-web-pro/hooks/useHighlightLine';
import type { UUID } from 'esm-web-pro/types/common';
import type { Lines, Points } from './CreateCable';


type Props = {
    points: Points,
    lines: Lines,
    onClick: Function,
    onContinuousPipe: (points: Array<Object>, lines: Array<Object>) => void,
    allowedForkIdentities: UUID[],

    leaflet: LeafletContext,
};

const MAX_CABLE_LINE_OBJECTS_COUNT = 50;

/*
 * Responsible for cable visualisation
 * Hold the whole cable path in its state
 */
const CreateCableVisualisation = (props: Props) => {
    const {
        points, lines, onClick, leaflet: { map }, onContinuousPipe, allowedForkIdentities, provider,
    } = props;
    const editor = React.useContext(EditorContext);
    const [cablePath, setCablePath] = React.useState([]);

    const checkIfPointIsSelectable = React.useCallback((newPoint, lastPointLayer, pointsCount) => {
        const undefinedPoint = !newPoint;
        const lineCut = newPoint && (newPoint?.child.type === 'linecut');
        const fork = newPoint?.child.type !== 'fork'
            || (points.length > 0 && allowedForkIdentities.includes(newPoint.identity));

        // Find the last line that is not from the continuous pipe segments
        let lastLineIndex = lines.length - 1;
        while (lines[lastLineIndex]?.continuousPipe && lastLineIndex > 0) {
            lastLineIndex -= 1;
        }
        const lastLine = lines[lastLineIndex];

        const lastLineIsNotSelected = !isEmpty(lines) && !lastLine.value;
        const noLastPointOrCanConnect = !lastPointLayer
            || canConnect(lastPointLayer.feature.properties.point, newPoint);

        return !lastLineIsNotSelected && noLastPointOrCanConnect && !undefinedPoint && !lineCut && fork
            && pointsCount <= MAX_CABLE_LINE_OBJECTS_COUNT;
    }, [points, lines, allowedForkIdentities]);

    /*
     * Takes care for calculating cable path and finding the line layer
     * between the current and previous points
     */
    const handleClick = React.useCallback((e) => {
        const pointLayer = e.target;
        pointLayer.closeTooltip();
        pointLayer.off('click', handleClick);
        const pointColor = getPointColor(pointLayer);
        const pointProvider = getPointProvider(pointLayer);
        const pointCoordinates = [pointLayer.getLatLng().lat, pointLayer.getLatLng().lng];
        if (points.length === 0) {
            onClick({ layer: pointLayer, coordinates: pointCoordinates, color: pointColor }, null);
            return;
        }

        const lastPoint = points[points.length - 1];
        const lastPointCoordinates = lastPoint.coordinates;

        let lineLayer = getLineBetweenTwoPoints(
            map, [pointCoordinates[1], pointCoordinates[0]], [lastPointCoordinates[1], lastPointCoordinates[0]],
        );

        if (!lineLayer) {
            const { color } = provider;
            lineLayer = L.polyline([lastPointCoordinates, pointCoordinates], { color });
            lineLayer.isNew = true;
            lineLayer.addTo(editor.featureGroups[DRAW_FG].current.leafletElement);
            lineLayer.initialOptions = { color };
            lineLayer.feature = {
                geometry: {
                    type: 'LineString',
                },
                properties: {
                    color: '#0074d9',
                },
            };
        }

        let lineCoordinates = lineLayer.toGeoJSON().geometry.coordinates.map((c) => [c[1], c[0]]);
        if (!lineLayer.isNew && isEqual(lineLayer.toGeoJSON().geometry.coordinates[0],
            pointLayer.toGeoJSON().geometry.coordinates)) {
            lineCoordinates = lineCoordinates.reverse();
        }

        onClick(
            {
                layer: pointLayer, coordinates: pointCoordinates, color: pointColor, provider: pointProvider,
            },
            { path: lineCoordinates, layer: lineLayer },
        );
    }, [points, onClick]);

    /*
     * Update cable path when points or lines update
     */
    React.useEffect(() => {
        const newCablePath = [];
        // Add points and lines coordinates to cable path by excluding lines first and last coordinates
        points.forEach((point, index) => {
            newCablePath.push(point.coordinates);
            if (index < lines.length) {
                if (isEqual(lines[index].path[0], point.coordinates)) {
                    const linePath = lines[index].path.slice(1, lines[index].path.length - 1);
                    newCablePath.push(...linePath);
                } else {
                    const linePath = [...lines[index].path].reverse().slice(1, lines[index].path.length - 1);
                    newCablePath.push(...linePath);
                }
            }
        });
        setCablePath(newCablePath);
    }, [points, lines]);

    /*
     * Register callbacks for updating points icons and click handlers
     */
    React.useEffect(() => {
        // Register callback for registering on click handlers for points that the cable can go through
        const pointClickCallback = createLineCallback(points, checkIfPointIsSelectable, handleClick);
        editor.registerLoadingCallback(pointClickCallback);

        // Register callback for visualising the points that cable go through with numbers
        const pointsNumbers = points.map((p) => p.pointNumber);
        const highlightPointsCallback = pointsCoordinatesHighlightCallback(
            points.map((p) => p.coordinates), handleClick, pointsNumbers,
        );
        editor.registerLoadingCallback(highlightPointsCallback);

        editor.reloadFeatures();
        return () => {
            editor.unregisterLoadingCallback(pointClickCallback);
            editor.unregisterLoadingCallback(highlightPointsCallback);
        };
    }, [cablePath, points, handleClick, allowedForkIdentities]);

    /*
     * Register callback for lines highlight
     */
    React.useEffect(() => {
        const highlightCableCallback = highlightCable(cablePath);
        editor.registerLoadingCallback(highlightCableCallback);

        let highlightSegmentCallback;
        const lastLine = lines.length > 0 ? lines[lines.length - 1] : null;
        if (lastLine && !lastLine.value && !lastLine.continuousPipe) {
            highlightSegmentCallback = UseHighlightLine(
                lastLine.path, [{ color: 'red' }, { weight: 3 }],
            );
            editor.registerLoadingCallback(highlightSegmentCallback);
        }

        editor.reloadFeatures();
        return () => {
            editor.unregisterLoadingCallback(highlightCableCallback);
            if (highlightSegmentCallback) {
                editor.unregisterLoadingCallback(highlightSegmentCallback);
            }
        };
    }, [cablePath, lines, points]);

    /*
     * Find points and layers when the last line is a segment from a continuous pipe
     */
    React.useEffect(() => {
        // If the last line has a pipes in options find points layers and the line layers between them that form
        // the continuous pipe
        if (lines.length === 0) return;
        const lastLine = lines[lines.length - 1];
        if (!lastLine.value) return;
        const lastLineSecondPoint = points[points.length - 1];

        const { pipes, path } = lastLine.options[lastLine.value];
        if (pipes) {
            const pointsFromContinuousPipe = [lastLineSecondPoint];
            const linesFromContinuousPipe = [];

            // Look for the point layers from the third coordinate, because the first two are already selected
            path.slice(lastLine.path.length).forEach((coordinates, index) => {
                const reverseCoordinates = [coordinates[1], coordinates[0]];
                const pipeEntry = Object.entries(pipes)
                    .find(([key, pipe]) => isEqual(pipe.path[0], coordinates)
                        || isEqual(pipe.path[pipe.path.length - 1], coordinates));

                if (pipeEntry) {
                    const [pipeUuid, currentPipe] = pipeEntry;
                    pointsFromContinuousPipe.push({ coordinates: reverseCoordinates });
                    const layer = get(map, ['coordsToLayers', `${coordinates[0]}-${coordinates[1]}`, 'point']);
                    if (layer) {
                        pointsFromContinuousPipe[pointsFromContinuousPipe.length - 1].layer = layer;
                    } else if (isEqual(currentPipe.start_point?.coordinates, coordinates)) {
                        pointsFromContinuousPipe[pointsFromContinuousPipe.length - 1]
                            .uuid = currentPipe.start_point.uuid;
                    } else if (isEqual(currentPipe.end_point?.coordinates, coordinates)) {
                        pointsFromContinuousPipe[pointsFromContinuousPipe.length - 1]
                            .uuid = currentPipe.end_point.uuid;
                    }
                }
            });

            // Look for the line layers that correspond to continuous pipe segments
            Object.entries(pipes).slice(1).forEach(([key, pipe], i) => {
                let pipePath = pipe.path.map((c) => [c[1], c[0]]);
                const line = { path: pipePath, line_object: key };
                let startCoordinates;
                if (isEmpty(linesFromContinuousPipe)) {
                    startCoordinates = points[points.length - 1].coordinates;
                } else {
                    const lastLinePath = linesFromContinuousPipe[linesFromContinuousPipe.length - 1].path;
                    startCoordinates = lastLinePath[lastLinePath.length - 1];
                }

                const startLines = get(map,
                    ['coordsToLayers', `${pipePath[0][0]}-${pipePath[0][1]}`,
                        'lines']);
                const endLines = get(map,
                    ['coordsToLayers', `${pipePath[pipePath.length - 1][0]}-${pipePath[pipePath.length - 1][1]}`,
                        'lines']);
                const layer = startLines?.find((l) => endLines?.includes(layer));
                if (layer) {
                    if (!L.latLng(startCoordinates).equals(L.latLng(pipePath[0]))) {
                        pipePath = pipePath.slice().reverse();
                    }
                    line.layer = layer;
                }
                linesFromContinuousPipe.push(line);
            });

            onContinuousPipe(
                pointsFromContinuousPipe,
                linesFromContinuousPipe,
            );
        }
    }, [lines]);

    return null;
};

export default withLeaflet(CreateCableVisualisation);
